import React from "react";
import { useSelector } from "react-redux";
import {
    Box,
    Button,
    IconButton,
    Snackbar,
    Slide,
    Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { TransitionProps } from "@mui/material/transitions";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { RootState } from "../../../redux/store";
import {
    AuditCompletionEvent,
    clearAuditCompletionEvent,
    getRecentAiAuditDetailsByIdData,
} from "../../../redux/slice/AI/AiAuditSlice";
import MedicalNoteAuditPopup from "./MedicalNoteAuditPopup";

const T = {
    bgPanel: "#18242f",
    bgInput: "#26384b",
    border: "rgba(255,255,255,0.10)",
    borderFocus: "rgba(0,210,180,0.45)",
    textPrimary: "#e8f0f8",
    textSecondary: "#7a92a8",
    accent: "#00d2b4",
    accentB: "#00b8f5",
};

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement<any, any> },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="down" ref={ref} {...props} />;
});

const MedicalNoteAuditCompletionNotification: React.FC = () => {
    const dispatch = useAppDispatch();
    const event = useSelector<RootState, AuditCompletionEvent | null>(
        (state) => state.aiAuditSlice.auditCompletionEvent,
    );

    const [popupOpen, setPopupOpen] = React.useState(false);
    const [popupAuditData, setPopupAuditData] = React.useState<any>(null);

   
    const cleanPatientName = React.useMemo(() => {
        if (!event?.patientName) return "";
        return event.patientName
            .split(/\s+/)
            .filter(
                (token) =>
                    token &&
                    token.toLowerCase() !== "null" &&
                    token.toLowerCase() !== "undefined",
            )
            .join(" ")
            .trim();
    }, [event?.patientName]);

    const handleClose = () => {
        dispatch(clearAuditCompletionEvent());
    };

    const handleViewReport = async () => {
        if (!event) return;

        try {
            const result = await dispatch(
                getRecentAiAuditDetailsByIdData(event.auditId),
            );
            if (result.meta.requestStatus === "fulfilled") {
                setPopupAuditData(result.payload?.data);
                setPopupOpen(true);
            } else {
                setPopupAuditData(event.auditData);
                setPopupOpen(true);
            }
        } catch (e) {
            setPopupAuditData(event.auditData);
            setPopupOpen(true);
        } finally {
            dispatch(clearAuditCompletionEvent());
        }
    };

    const handleClosePopup = () => {
        setPopupOpen(false);
        setPopupAuditData(null);
    };

    return (
        <>
            <Snackbar
                open={!!event}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                TransitionComponent={Transition}
                onClick={(e) => e.stopPropagation()}
            >
                <Box
                    sx={{
                        position: "relative",
                        bgcolor: T.bgPanel,
                        border: `1px solid ${T.border}`,
                        borderRadius: "12px",
                        overflow: "hidden",
                        boxShadow:
                            "0 8px 32px rgba(0,0,0,0.50), 0 1px 0 rgba(255,255,255,0.04) inset",
                        maxWidth: 480,
                        minWidth: 340,
                        display: "flex",
                        flexDirection: "column",
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "2px",
                            background: `linear-gradient(90deg, transparent, ${T.accent} 35%, ${T.accentB} 65%, transparent)`,
                            opacity: 0.85,
                        },
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: 2.5,
                            pt: 2.5,
                            pb: 1.8,
                            borderBottom: `1px solid ${T.border}`,
                        }}
                    >
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: "10px",
                                background: `${T.accent}12`,
                                border: `1px solid ${T.accent}30`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                boxShadow: `0 0 12px rgba(0,210,180,0.18)`,
                            }}
                        >
                            <CheckCircleIcon sx={{ color: T.accent, fontSize: 20 }} />
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography
                                fontWeight={700}
                                sx={{
                                    fontSize: "1rem",
                                    color: T.textPrimary,
                                    lineHeight: 1.3,
                                }}
                            >
                                Medical Note Audit Completed
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "0.78rem",
                                    color: T.textSecondary,
                                    mt: 0.1,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {cleanPatientName
                                    ? `Patient: ${cleanPatientName}`
                                    : "Your audit report is ready."}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleClose}
                            size="small"
                            sx={{
                                width: 28,
                                height: 28,
                                ml: "auto",
                                flexShrink: 0,
                                border: `1px solid ${T.border}`,
                                bgcolor: T.bgInput,
                                borderRadius: "7px",
                                color: T.textSecondary,
                                transition: "all 0.13s",
                                "&:hover": {
                                    borderColor: T.borderFocus,
                                    color: T.textPrimary,
                                    bgcolor: T.bgInput,
                                },
                            }}
                        >
                            <CloseIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                    </Box>

                    {/* Body */}
                    <Box
                        sx={{
                            px: 2.5,
                            py: 2,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        {event?.fileName && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.4,
                                    background: "rgba(0,210,180,0.07)",
                                    border: `1px solid rgba(0,210,180,0.18)`,
                                    borderRadius: "10px",
                                    px: 1.6,
                                    py: 1,
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: T.textSecondary,
                                        fontSize: "0.75rem",
                                        flexShrink: 0,
                                    }}
                                >
                                    File:
                                </Typography>
                                <Typography
                                    sx={{
                                        color: T.accent,
                                        fontWeight: 500,
                                        fontSize: "0.85rem",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                    title={event.fileName}
                                >
                                    {event.fileName}
                                </Typography>
                            </Box>
                        )}
                        <Typography
                            variant="body2"
                            sx={{ color: T.textSecondary, lineHeight: 1.5, mt: 0.5 }}
                        >
                            The medical audit report is ready for review. Click{" "}
                            <Box
                                component="span"
                                sx={{ color: T.textPrimary, fontWeight: 600 }}
                            >
                                View Report
                            </Box>{" "}
                            to see the details.
                        </Typography>
                    </Box>

                    {/* Footer */}
                    <Box
                        sx={{
                            px: 2.5,
                            pt: 1.5,
                            pb: 2.2,
                            borderTop: `1px solid ${T.border}`,
                            display: "flex",
                            gap: 1.5,
                            justifyContent: "flex-end",
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            sx={{
                                px: 2.2,
                                py: 0.75,
                                borderRadius: "8px",
                                fontSize: "0.82rem",
                                fontWeight: 600,
                                textTransform: "none",
                                border: `1px solid ${T.border}`,
                                bgcolor: T.bgInput,
                                color: T.textSecondary,
                                "&:hover": {
                                    bgcolor: T.bgInput,
                                    borderColor: "rgba(255,255,255,0.18)",
                                    color: T.textPrimary,
                                },
                            }}
                        >
                            Dismiss
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<VisibilityIcon sx={{ fontSize: 18 }} />}
                            onClick={handleViewReport}
                            sx={{
                                px: 2.4,
                                py: 0.85,
                                borderRadius: "8px",
                                fontSize: "0.85rem",
                                fontWeight: 700,
                                textTransform: "none",
                                background: `linear-gradient(135deg, ${T.accent}, ${T.accentB})`,
                                color: "#0b1a26",
                                boxShadow: "0 4px 14px rgba(0,210,180,0.25)",
                                "&:hover": {
                                    background: `linear-gradient(135deg, ${T.accent}, ${T.accentB})`,
                                    filter: "brightness(1.08)",
                                    boxShadow: "0 6px 18px rgba(0,210,180,0.35)",
                                },
                            }}
                        >
                            View Report
                        </Button>
                    </Box>
                </Box>
            </Snackbar>

            {/* dialogSx raises the z-index above MUI's default modal
                (1300). This ensures the report popup opens IN FRONT of
                the InFlightButton's "Medical Note Audits — In Progress"
                list popup if that list happens to be open when the user
                clicks View Report — without this override the list
                Portal mounts later in DOM and ends up stacking on top
                even though both share z-index 1300. */}
            <MedicalNoteAuditPopup
                open={popupOpen}
                onClose={handleClosePopup}
                initialAuditData={popupAuditData}
                auditCompleted={true}
                uploadedPdfFile={null}
                dialogSx={{ zIndex: (t: any) => t.zIndex.modal + 10 }}
            />
        </>
    );
};

export default MedicalNoteAuditCompletionNotification;
